import type {
  MarketCategory,
  MarketCategoryPage,
  MarketFestivalTab,
  MarketPortalData,
  MarketScene,
  MarketTemplate,
  MarketTradeRow
} from './types'

const CATEGORY_DEFS: Omit<MarketCategory, 'desc_ids'>[] = [
  { id: 1, name: '活动抽奖', alias: '抽奖活动', color: '#FF6C6C', icon: 'youxichoujiang', visible: true },
  { id: 2, name: '裂变引流', alias: '裂变活动', color: '#FFB83B', icon: 'liebianyinliu', visible: true },
  { id: 4, name: '商业促销', alias: '促销活动', color: '#FF7D41', icon: 'shangyecuxiao', visible: true },
  { id: 1024, name: '红包活动', alias: null, color: '#F04E4E', icon: 'hongbaohuodong', visible: true },
  { id: 8, name: '长期活动', alias: null, color: '#6854F2', icon: 'changqihuodong', visible: true },
  { id: 16, name: '投票竞猜', alias: null, color: '#32CE73', icon: 'toupiaohuodong', visible: true },
  { id: 32, name: '答题活动', alias: null, color: '#2966FF', icon: 'datihuodong', visible: true },
  { id: 2048, name: 'AR活动', alias: null, color: '#ffb83b', icon: 'arhuodong', visible: true },
  { id: 128, name: '现场活动', alias: null, color: '#FF7D41', icon: 'xianchanghuodong', visible: true }
]

const TEMPLATE_NAMES = [
  '全民世界杯狂欢大抽奖',
  '幸运大转盘',
  '刮刮乐赢好礼',
  '九宫格抽奖',
  '答题赢红包',
  '裂变助力领券',
  '拼团砍价',
  '限时秒杀',
  '签到领积分',
  '投票选冠军',
  '集卡兑大奖',
  '盲盒开箱',
  '红包雨',
  '摇一摇抽奖',
  '打卡闯关',
  '趣味测试',
  '618狂欢购',
  '中秋博饼',
  '国庆七天乐',
  '圣诞许愿树',
  '新年开运签',
  '女神节礼遇',
  '520甜蜜告白',
  '暑期清凉节'
]

const SCENES: MarketScene[] = [
  { key: 1, name: '门店引流' },
  { key: 2, name: '线上传播' },
  { key: 3, name: '会员促活' },
  { key: 4, name: '节日营销' },
  { key: 5, name: '新品发布', hide_in_activity: true },
  { key: 6, name: '直播互动' }
]

const FESTIVAL_TABS: MarketFestivalTab[] = [
  { name: '春节', ids: [] },
  { name: '情人节', ids: [] },
  { name: '618', ids: [] },
  { name: '中秋', ids: [] },
  { name: '国庆', ids: [] },
  { name: '圣诞', ids: [] }
]

const TRADE_ROWS: MarketTradeRow[] = [
  { name: '零售百货', ids: [] },
  { name: '餐饮美食', ids: [] },
  { name: '美妆护肤', ids: [] },
  { name: '汽车出行', ids: [] },
  { name: '金融保险', ids: [] },
  { name: '教育培训', ids: [] },
  { name: '房产家居', ids: [] },
  { name: '文旅景区', ids: [] }
]

/** 生成 mock 模板库（约 72 条，足够首页/分类/搜索演示） */
function buildTemplates(): Record<number, MarketTemplate> {
  const map: Record<number, MarketTemplate> = {}
  for (let i = 1; i <= 72; i++) {
    const name = TEMPLATE_NAMES[(i - 1) % TEMPLATE_NAMES.length]
    const suffix = i > TEMPLATE_NAMES.length ? ` · 方案${i}` : ''
    map[i] = {
      id: i,
      name: `${name}${suffix}`,
      brief: ['互动玩法丰富', '一键创建活动', '支持分享裂变', '适配移动端 H5'][(i - 1) % 4],
      hotScore: Math.max(0, 100 - ((i * 7) % 95)),
      sceneKey: SCENES[(i - 1) % SCENES.length].key,
      coverUrl: `https://picsum.photos/seed/market-${i}/400/280`
    }
  }
  return map
}

function buildPortalData(): MarketPortalData {
  const templates = buildTemplates()
  const allIds = Object.keys(templates).map(Number)
  const hotSorted = [...allIds].sort((a, b) => templates[b].hotScore - templates[a].hotScore)

  FESTIVAL_TABS.forEach((tab, idx) => {
    tab.ids = hotSorted.slice(idx * 6, idx * 6 + 6)
  })
  TRADE_ROWS.forEach((row, idx) => {
    row.ids = allIds.slice(idx * 6, idx * 6 + 6)
  })

  const categories: MarketCategory[] = CATEGORY_DEFS.map((c, idx) => {
    const start = idx * 7
    return { ...c, desc_ids: allIds.slice(start, start + 3) }
  })

  const category_pages: MarketCategoryPage[] = categories.map((c) => ({
    id: c.id,
    name: c.name,
    ids: allIds.filter((_, i) => i % categories.length === categories.indexOf(c))
  }))

  return {
    categories,
    templates,
    festival_tabs: FESTIVAL_TABS,
    trade_rows: TRADE_ROWS,
    scenes: SCENES,
    category_pages,
    latest: hotSorted.slice(0, 1)
  }
}

export const MARKET_MOCK_DATA: MarketPortalData = buildPortalData()

/** uview-plus u-icon name，由 MarketUviewIcon 渲染 */
export const MARKET_CATEGORY_ICONS: Record<string, string> = {
  youxichoujiang: 'gift-fill',
  liebianyinliu: 'share-fill',
  shangyecuxiao: 'shopping-cart-fill',
  hongbaohuodong: 'red-packet-fill',
  changqihuodong: 'hourglass-half-fill',
  toupiaohuodong: 'thumb-up-fill',
  datihuodong: 'edit-pen-fill',
  arhuodong: 'scan',
  xianchanghuodong: 'star-fill',
  qiweigongju: 'chat-fill',
  siyushangcheng: 'bag-fill',
  H5huodong: 'grid-fill',
  scIcon: 'play-circle-fill'
}

export const MARKET_THEME_COLORS = [
  '#ff6c6c',
  '#ffa940',
  '#67c23a',
  '#2b6de8',
  '#8b6df0',
  '#f56c6c',
  '#13c2c2',
  '#303133'
]

export const MARKET_DEFAULT_PRIZES = [
  { name: '一等奖 iPhone 17', type: '实物', count: 1, probability: 0.5 },
  { name: '世界杯纪念球衣', type: '实物', count: 10, probability: 2 },
  { name: '50元话费', type: '虚拟', count: 50, probability: 5 },
  { name: '10元红包', type: '现金', count: 100, probability: 12.5 },
  { name: '5元优惠券', type: '优惠券', count: 200, probability: 20 },
  { name: '谢谢参与', type: '空', count: 0, probability: 60 }
]

export function getTemplate(id: number): MarketTemplate | undefined {
  return MARKET_MOCK_DATA.templates[id]
}

export function getTemplateName(id: number): string {
  return MARKET_MOCK_DATA.templates[id]?.name || `模板 #${id}`
}

export function getTemplateBrief(id: number): string {
  return MARKET_MOCK_DATA.templates[id]?.brief || '暂无简介'
}

export function getCoverUrl(id: number): string | undefined {
  return MARKET_MOCK_DATA.templates[id]?.coverUrl
}

export function getHotPool(): number[] {
  return Object.values(MARKET_MOCK_DATA.templates)
    .filter((t) => t.hotScore > 0)
    .sort((a, b) => b.hotScore - a.hotScore)
    .map((t) => t.id)
}

export function searchTemplateIds(keyword: string, limit = 20): number[] {
  const kw = keyword.trim().toLowerCase()
  if (!kw) return []
  return Object.values(MARKET_MOCK_DATA.templates)
    .filter((t) => `${t.name}${t.brief}`.toLowerCase().includes(kw))
    .slice(0, limit)
    .map((t) => t.id)
}

export function getCategoryPageIds(categoryId: number): number[] {
  if (categoryId === -1) {
    return MARKET_MOCK_DATA.festival_tabs.flatMap((f) => f.ids)
  }
  const page = MARKET_MOCK_DATA.category_pages.find((c) => c.id === categoryId)
  return page?.ids || []
}

export function sortTemplateIds(ids: number[], mode: 0 | 1 | 2): number[] {
  const list = [...ids]
  if (mode === 1) {
    list.sort((a, b) => (getTemplate(b)?.hotScore || 0) - (getTemplate(a)?.hotScore || 0))
  } else if (mode === 2) {
    list.reverse()
  }
  return list
}

export function paginateIds(ids: number[], page: number, pageSize = 10): { ids: number[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(ids.length / pageSize))
  const safePage = Math.min(Math.max(0, page), totalPages - 1)
  return {
    ids: ids.slice(safePage * pageSize, safePage * pageSize + pageSize),
    totalPages
  }
}

export function filterByScene(ids: number[], sceneKey: number): number[] {
  return ids.filter((id) => getTemplate(id)?.sceneKey === sceneKey)
}
